/**
 * Acquisition tracking - captures where users came from.
 * 
 * Captures on first visit:
 * - document.referrer (e.g., google.com, reddit.com)
 * - UTM parameters (utm_source, utm_medium, utm_campaign)
 * - Landing page URL
 * 
 * Stores in localStorage to persist across page loads,
 * then saved to Firestore when user document is created.
 */

const STORAGE_KEY = 'parkproof_acquisition';

export interface AcquisitionData {
  referrer: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  landingPage: string;
  timestamp: number;
}

/**
 * Parse referrer into a readable source name
 */
function parseReferrer(referrer: string): string | null {
  if (!referrer) return null;
  
  try {
    const url = new URL(referrer);
    const hostname = url.hostname.toLowerCase();
    
    // Known sources
    if (hostname.includes('google')) return 'google';
    if (hostname.includes('bing')) return 'bing';
    if (hostname.includes('duckduckgo')) return 'duckduckgo';
    if (hostname.includes('yahoo')) return 'yahoo';
    if (hostname.includes('facebook') || hostname.includes('fb.com')) return 'facebook';
    if (hostname.includes('instagram')) return 'instagram';
    if (hostname.includes('twitter') || hostname.includes('t.co') || hostname.includes('x.com')) return 'twitter';
    if (hostname.includes('linkedin')) return 'linkedin';
    if (hostname.includes('reddit')) return 'reddit';
    if (hostname.includes('youtube')) return 'youtube';
    if (hostname.includes('tiktok')) return 'tiktok';
    if (hostname.includes('chatgpt') || hostname.includes('openai')) return 'chatgpt';
    if (hostname.includes('claude') || hostname.includes('anthropic')) return 'claude';
    if (hostname.includes('perplexity')) return 'perplexity';
    
    // Return the domain for unknown sources
    return hostname.replace('www.', '');
  } catch {
    return referrer.slice(0, 50);
  }
}

/**
 * Get UTM parameters from URL
 */
function getUtmParams(): Pick<AcquisitionData, 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm'> {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
    utmContent: params.get('utm_content'),
    utmTerm: params.get('utm_term'),
  };
}

/**
 * Capture acquisition data on first visit.
 * Should be called once when the app loads.
 */
export function captureAcquisition(): void {
  // Only capture once per browser
  if (localStorage.getItem(STORAGE_KEY)) return;
  
  const utmParams = getUtmParams();
  const data: AcquisitionData = {
    referrer: parseReferrer(document.referrer),
    ...utmParams,
    landingPage: window.location.pathname + window.location.search,
    timestamp: Date.now(),
  };
  
  // Only store if we have some acquisition data
  if (data.referrer || data.utmSource) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } else {
    // Mark as captured even if direct traffic (no referrer)
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...data,
      referrer: 'direct',
    }));
  }
}

/**
 * Get stored acquisition data.
 * Returns null if not yet captured or already consumed.
 */
export function getAcquisitionData(): AcquisitionData | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored) as AcquisitionData;
  } catch {
    return null;
  }
}

/**
 * Format acquisition data for Firestore storage.
 * Returns a flat object suitable for storing in the user document.
 */
export function getAcquisitionForFirestore(): Record<string, string | number | null> | null {
  const data = getAcquisitionData();
  if (!data) return null;
  
  return {
    acquisitionSource: data.utmSource || data.referrer || 'direct',
    acquisitionMedium: data.utmMedium || (data.referrer ? 'referral' : 'none'),
    acquisitionCampaign: data.utmCampaign,
    acquisitionReferrer: data.referrer,
    acquisitionLandingPage: data.landingPage,
    acquisitionTimestamp: data.timestamp,
  };
}
